import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/shared/lib/auth-context';
import { logger } from '@/shared/utils/logger';
import { 
  primaryNavigationItems, 
  secondaryNavigationItems, 
  type NavigationItem 
} from '@/shared/config/navigationConfig';

export const useNavigation = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { t } = useTranslation();

  const handleLogout = useCallback(async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      logger.error('Logout failed', error);
      navigate('/login');
    }
  }, [logout, navigate]);

  const handleNavigation = useCallback((item: NavigationItem) => {
    if (item.action === 'logout') {
      handleLogout();
    } else if (item.path) {
      // Special handling for demo path - add timestamp to force reset
      if (item.path === '/demo') {
        navigate(`${item.path}?reset=${Date.now()}`);
      } else {
        navigate(item.path);
      }
    }
  }, [navigate, handleLogout]);

  const createMenuItems = useCallback((items: NavigationItem[]) => {
    return items.map(item => ({
      key: item.key,
      icon: item.icon,
      label: t(item.labelKey),
      onClick: () => handleNavigation(item),
    }));
  }, [t, handleNavigation]);

  const primaryMenuItems = createMenuItems(primaryNavigationItems);
  const secondaryMenuItems = createMenuItems(secondaryNavigationItems);

  return {
    primaryMenuItems,
    secondaryMenuItems,
    handleLogout,
  };
};